import { Autorenew as ActiveIcon } from "@mui/icons-material";
import { Badge, IconButton, Tooltip } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useActiveRuns } from "../../hooks/queries/useActiveRuns";
import ActiveRunsModal from "./ActiveRunsModal";

/**
 * Global indicator (for the app sidebar) showing how many tracks/projects have
 * runs in flight right now. Polls continuously and opens the Active Runs modal
 * on click.
 */
const ActiveRunsIndicator: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { data } = useActiveRuns(true); // always poll for the live count

  const { trackCount, projectCount, total } = useMemo(() => {
    const tracks = new Set<string>();
    const projects = new Set<string>();
    const rows = [...(data?.discovery ?? []), ...(data?.evaluation ?? [])];
    rows.forEach((r) => {
      tracks.add(r.track_id);
      projects.add(r.project_id);
    });
    return {
      trackCount: tracks.size,
      projectCount: projects.size,
      total: rows.length,
    };
  }, [data]);

  const tooltip =
    trackCount > 0
      ? `${total} active run${total === 1 ? "" : "s"} across ${trackCount} track${
          trackCount === 1 ? "" : "s"
        } / ${projectCount} project${projectCount === 1 ? "" : "s"}`
      : "No active runs";

  return (
    <>
      <Tooltip title={tooltip} placement="bottom" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{ color: trackCount > 0 ? "success.main" : "text.secondary" }}
        >
          <Badge
            badgeContent={trackCount}
            color="success"
            invisible={trackCount === 0}
          >
            <ActiveIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <ActiveRunsModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default ActiveRunsIndicator;
